import { useCallback, useEffect, useState } from "react";
import { CreditCard, Loader2, RefreshCw, CheckCircle2, AlertTriangle, Ban, Undo2, ExternalLink } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../ui/alert-dialog";
import { toast } from "sonner";

const API = "/api";
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }) : "—");
const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString("en-AU", { dateStyle: "medium" }) : "—");
const money = (amount, currency) => (amount == null ? "—" : `$${Number(amount).toFixed(2)}${currency ? ` ${currency}` : ""}`);
const TIER_LABEL = { free: "Free", student: "Student", school: "Teacher Starter", teacher_plus: "Teacher Plus", teacher_pro: "Teacher Pro" };

function StatusBadge({ sub }) {
  if (sub.keep) return <Badge className="bg-emerald-500/15 text-emerald-700 border-emerald-500/40 dark:text-emerald-400" data-testid={`stripe-sub-keep-${sub.subscription_id}`}>Account is on this one</Badge>;
  if (sub.duplicate) return <Badge variant="destructive" data-testid={`stripe-sub-duplicate-${sub.subscription_id}`}>Duplicate</Badge>;
  return <Badge variant="outline" className="capitalize">{sub.status}</Badge>;
}

function SubscriptionRow({ sub, busy, onCancel, onRefund }) {
  const inv = sub.latest_invoice;
  const canRefund = inv?.payment_intent_id && !inv.refunded;
  return (
    <li className={`rounded-lg border px-3 py-2.5 text-sm space-y-1.5 ${sub.duplicate ? "border-red-300 bg-red-50/60 dark:bg-red-900/10 dark:border-red-800" : sub.keep ? "border-emerald-300 bg-emerald-50/50 dark:bg-emerald-900/10 dark:border-emerald-800" : "border-border/60 bg-card"}`} data-testid={`stripe-sub-${sub.subscription_id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <StatusBadge sub={sub} />
        {!sub.keep && !sub.duplicate ? null : <Badge variant="outline" className="capitalize">{sub.status}</Badge>}
        <span className="font-medium">{sub.plan_name || sub.plan || "Unknown plan"}</span>
        <span className="text-muted-foreground">{money(sub.amount, sub.currency)}/month</span>
        {sub.cancel_at_period_end && <Badge variant="outline" className="text-amber-600 border-amber-400">Ends {fmtDay(sub.current_period_end)}</Badge>}
      </div>
      <p className="text-xs text-muted-foreground break-all">
        <code>{sub.subscription_id}</code> · customer <code>{sub.customer_id}</code>{sub.customer_email ? ` (${sub.customer_email})` : ""} · started {fmtWhen(sub.created)}
        {sub.canceled_at ? ` · canceled ${fmtWhen(sub.canceled_at)}` : ""}
      </p>
      {inv && (
        <p className="text-xs flex items-center gap-2 flex-wrap" data-testid={`stripe-sub-invoice-${sub.subscription_id}`}>
          <span>Latest payment: <span className="font-medium">{money(inv.amount_paid, inv.currency)}</span> {inv.paid_at ? `on ${fmtDay(inv.paid_at)}` : `(${inv.status || "no payment"})`}</span>
          {inv.refunded ? <Badge className="bg-sky-500/15 text-sky-700 border-sky-500/40 dark:text-sky-400">Refunded</Badge> : inv.refunded_amount > 0 ? <Badge variant="outline">Partly refunded {money(inv.refunded_amount, inv.currency)}</Badge> : null}
        </p>
      )}
      {sub.duplicate && (
        <div className="flex items-center gap-2 pt-1">
          {sub.live && (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => onCancel(sub)} className="gap-1.5 border-red-300 text-red-700 hover:bg-red-50 dark:hover:bg-red-950/40" data-testid={`stripe-cancel-${sub.subscription_id}`}>
              <Ban className="w-3.5 h-3.5" /> Cancel subscription
            </Button>
          )}
          {canRefund && (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => onRefund(sub)} className="gap-1.5 border-sky-300 text-sky-700 hover:bg-sky-50 dark:hover:bg-sky-950/40" data-testid={`stripe-refund-${sub.subscription_id}`}>
              <Undo2 className="w-3.5 h-3.5" /> Refund {money(inv.amount_paid, inv.currency)}
            </Button>
          )}
        </div>
      )}
    </li>
  );
}

function AccountSummary({ panel, busy, onSync }) {
  const u = panel.user;
  const keep = panel.subscriptions.find((s) => s.keep);
  return (
    <div className={`rounded-lg border p-3 text-sm flex items-start justify-between gap-3 flex-wrap ${panel.in_sync ? "border-emerald-300 bg-emerald-50/50 dark:bg-emerald-900/10 dark:border-emerald-800" : "border-amber-300 bg-amber-50/60 dark:bg-amber-900/10 dark:border-amber-800"}`} data-testid="stripe-account-summary">
      <div className="space-y-1 min-w-0">
        <p className="flex items-center gap-2 font-medium">
          {panel.in_sync ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertTriangle className="w-4 h-4 text-amber-600" />}
          {panel.in_sync ? "Account matches Stripe" : keep ? `Out of sync - account shows ${TIER_LABEL[u.subscription_tier] || u.subscription_tier || "Free"}, Stripe has a live ${keep.plan_name || keep.plan} subscription` : `Account shows ${TIER_LABEL[u.subscription_tier] || u.subscription_tier || "Free"} but Stripe has no live subscription`}
        </p>
        <p className="text-xs text-muted-foreground break-all">
          Plan on account: <span className="font-medium text-foreground" data-testid="stripe-account-tier">{TIER_LABEL[u.subscription_tier] || u.subscription_tier || "Free"}</span> · status {u.subscription_status || "inactive"}
          {u.subscription_id ? <> · subscription <code>{u.subscription_id}</code></> : null}
          {u.stripe_customer_id ? <> · customer <code>{u.stripe_customer_id}</code></> : null}
        </p>
      </div>
      {!panel.in_sync && keep && (
        <Button size="sm" disabled={busy} onClick={onSync} className="gap-1.5" data-testid="stripe-sync-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Sync with Stripe
        </Button>
      )}
    </div>
  );
}

export function StripeSyncDialog({ userId, userLabel, open, onOpenChange, onChanged }) {
  const [panel, setPanel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // {kind: "cancel"|"refund", sub}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/stripe/users/${userId}`, { credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail || "Could not load Stripe details"); onOpenChange(false); return; }
      setPanel(data);
    } catch { toast.error("Could not load Stripe details"); } finally { setLoading(false); }
  }, [userId, onOpenChange]);

  useEffect(() => { if (open && userId) { setPanel(null); load(); } }, [open, userId, load]);

  const act = async (path, body, okMsg) => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/stripe/users/${userId}/${path}`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: body ? JSON.stringify(body) : undefined });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail || "Stripe action failed"); return; }
      setPanel(data);
      toast.success(okMsg(data));
      onChanged?.();
    } catch { toast.error("Stripe action failed"); } finally { setBusy(false); setConfirm(null); }
  };

  const sync = () => act("sync", null, (d) => d.result?.activated ? `Account moved to ${TIER_LABEL[d.user.subscription_tier] || d.user.subscription_tier}` : d.result?.duplicate ? "Duplicate found and stopped - refund below" : "Account already matched Stripe");
  const cancel = (sub) => act("cancel", { subscription_id: sub.subscription_id }, () => "Duplicate subscription canceled - it won't renew");
  const refund = (sub) => act("refund", { subscription_id: sub.subscription_id }, (d) => `Refunded ${money(d.result?.refund?.amount, d.result?.refund?.currency)}`);

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="stripe-sync-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CreditCard className="w-5 h-5 text-primary" /> Stripe · {userLabel}</DialogTitle>
          <DialogDescription>Everything Stripe has for this account's email. Sync fixes a plan the webhook missed; cancel and refund only ever apply to duplicates.</DialogDescription>
        </DialogHeader>
        {loading || !panel ? (
          <p className="text-sm text-muted-foreground flex items-center gap-2 py-6" data-testid="stripe-panel-loading"><Loader2 className="w-4 h-4 animate-spin" /> Asking Stripe…</p>
        ) : (
          <div className="space-y-4">
            <AccountSummary panel={panel} busy={busy} onSync={sync} />
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Stripe subscriptions ({panel.subscriptions.length})</p>
              {panel.subscriptions.length === 0 ? (
                <p className="text-sm text-muted-foreground border rounded-lg p-3" data-testid="stripe-no-subs">No Stripe subscriptions found for {panel.user.email}.</p>
              ) : (
                <ul className="space-y-2" data-testid="stripe-sub-list">{panel.subscriptions.map((s) => <SubscriptionRow key={s.subscription_id} sub={s} busy={busy} onCancel={(sub) => setConfirm({ kind: "cancel", sub })} onRefund={(sub) => setConfirm({ kind: "refund", sub })} />)}</ul>
              )}
            </div>
            {panel.transactions.length > 0 && (
              <details className="text-xs text-muted-foreground" data-testid="stripe-transactions">
                <summary className="cursor-pointer select-none">Checkout history on this site ({panel.transactions.length})</summary>
                <ul className="mt-2 space-y-1">
                  {panel.transactions.map((t) => (
                    <li key={t.session_id} className="flex gap-2 flex-wrap"><span>{fmtWhen(t.created_at)}</span><span className="font-medium text-foreground">{TIER_LABEL[t.plan] || t.plan}</span><span className="capitalize">{t.payment_status}</span>{t.activated_by && <span>· activated by {t.activated_by}</span>}{t.duplicate_of && <span className="text-red-600">· duplicate{t.refund_needed ? " (refund needed)" : ""}</span>}</li>
                  ))}
                </ul>
              </details>
            )}
            <a href="https://dashboard.stripe.com/customers" target="_blank" rel="noopener noreferrer" className="text-xs text-primary inline-flex items-center gap-1 hover:underline">Open Stripe Dashboard <ExternalLink className="w-3 h-3" /></a>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="stripe-dialog-close">Close</Button>
        </DialogFooter>
      </DialogContent>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="stripe-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.kind === "refund"
                ? `This refunds ${money(confirm.sub.latest_invoice?.amount_paid, confirm.sub.latest_invoice?.currency)} to the card used for the duplicate subscription ${confirm.sub.subscription_id}. Money leaves your Stripe balance immediately and this cannot be undone.`
                : `This cancels the duplicate subscription ${confirm?.sub.subscription_id} immediately so it never renews. The account stays on its original subscription. It does not refund anything - use Refund for that.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="stripe-confirm-cancel">Keep as is</AlertDialogCancel>
            <AlertDialogAction onClick={() => (confirm.kind === "refund" ? refund(confirm.sub) : cancel(confirm.sub))} className={confirm?.kind === "refund" ? "bg-sky-600 hover:bg-sky-700" : "bg-red-600 hover:bg-red-700"} data-testid="stripe-confirm-ok">
              {confirm?.kind === "refund" ? "Refund now" : "Cancel subscription"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
